import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Play, Upload, Lock, Trash2, X } from "lucide-react";
import { Reveal, Overline } from "./Reveal";
import { API } from "@/config/site";

const SLOTS = [1, 2, 3, 4, 5, 6];

export default function Testimonials() {
  const [items, setItems] = useState([]);
  const [passcode, setPasscode] = useState("");
  const [isAdmin, setIsAdmin] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const [editing, setEditing] = useState(null); // slot number
  const fileRef = useRef(null);

  const load = async () => {
    try {
      const res = await axios.get(`${API}/testimonials`);
      setItems(res.data);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const bySlot = (n) => items.find((it) => it.slot === n);

  const doLogin = () => {
    if (!passcode.trim()) return;
    setIsAdmin(true);
    setShowLogin(false);
    toast.success("Admin mode enabled");
  };

  const handleFile = async (slot, file) => {
    if (!file) return;
    const form = new FormData();
    form.append("slot", slot);
    const existing = bySlot(slot);
    form.append("name", existing?.name || "Student");
    form.append("role", existing?.role || "Investrading Member");
    form.append("file", file);
    try {
      toast.loading("Uploading video…", { id: "up" });
      await axios.post(`${API}/testimonials`, form, {
        headers: { "X-Admin-Passcode": passcode },
      });
      toast.success("Testimonial uploaded", { id: "up" });
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed", { id: "up" });
    }
  };

  const removeVideo = async (slot) => {
    try {
      await axios.delete(`${API}/testimonials/${slot}`, {
        headers: { "X-Admin-Passcode": passcode },
      });
      toast.success("Removed");
      load();
    } catch (e) {
      toast.error("Failed to remove");
    }
  };

  return (
    <section id="testimonials" data-testid="testimonials-section" className="relative py-28 md:py-40 bg-surface/30 border-y border-white/10">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
          <Reveal className="max-w-xl">
            <Overline>Results</Overline>
            <h2 className="font-display text-4xl md:text-6xl tracking-tight leading-tight text-white mt-5">
              Voices from the cohort.
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <button
              data-testid="admin-toggle"
              onClick={() => (isAdmin ? setIsAdmin(false) : setShowLogin(true))}
              className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-slate-500 hover:text-burgundy transition-colors duration-300"
            >
              <Lock size={13} /> {isAdmin ? "Exit admin" : "Admin upload"}
            </button>
          </Reveal>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {SLOTS.map((n, idx) => {
            const t = bySlot(n);
            const hasVideo = t && t.video_id;
            return (
              <Reveal key={n} delay={idx * 0.05}>
                <div
                  data-testid={`testimonial-slot-${n}`}
                  className="group relative aspect-[9/16] rounded-2xl border border-white/10 bg-ink overflow-hidden"
                >
                  {hasVideo ? (
                    <video
                      data-testid={`testimonial-video-${n}`}
                      src={`${API}/testimonials/${n}/video`}
                      controls
                      playsInline
                      className="w-full h-full object-contain bg-black"
                    />
                  ) : (
                    <div className="w-full h-full flex flex-col items-center justify-center text-center px-6">
                      <div className="h-14 w-14 rounded-full border border-white/15 flex items-center justify-center text-slate-500 group-hover:text-burgundy group-hover:border-burgundy/50 transition-colors duration-300">
                        <Play size={20} />
                      </div>
                      <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500 mt-5">
                        Testimonial {String(n).padStart(2, "0")}
                      </p>
                      <p className="text-slate-600 text-xs mt-2">Video coming soon</p>
                    </div>
                  )}

                  {isAdmin && (
                    <div className="absolute top-3 right-3 flex gap-2">
                      <button
                        data-testid={`upload-btn-${n}`}
                        onClick={() => setEditing(n)}
                        className="h-9 w-9 rounded-full bg-burgundy hover:bg-burgundy-hover flex items-center justify-center text-ink transition-colors duration-300"
                        title="Upload video"
                      >
                        <Upload size={15} />
                      </button>
                      {hasVideo && (
                        <button
                          onClick={() => removeVideo(n)}
                          className="h-9 w-9 rounded-full bg-ink/80 border border-white/15 hover:border-burgundy flex items-center justify-center text-slate-200 transition-colors duration-300"
                          title="Remove"
                        >
                          <Trash2 size={15} />
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>

      {/* Admin login modal */}
      {showLogin && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-ink/80 backdrop-blur-sm px-6" data-testid="admin-login-modal">
          <div className="w-full max-w-sm rounded-2xl border border-white/10 bg-surface p-8 relative">
            <button onClick={() => setShowLogin(false)} className="absolute top-4 right-4 text-slate-400 hover:text-white">
              <X size={18} />
            </button>
            <h3 className="font-display text-2xl text-white">Admin access</h3>
            <p className="text-slate-400 text-sm mt-2">Enter the passcode to upload testimonial videos.</p>
            <input
              data-testid="admin-passcode-input"
              type="password"
              value={passcode}
              onChange={(e) => setPasscode(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && doLogin()}
              placeholder="Passcode"
              className="w-full mt-5 bg-ink border border-white/15 rounded-lg px-4 py-3 text-white outline-none focus:border-burgundy transition-colors"
            />
            <button
              data-testid="admin-login-btn"
              onClick={doLogin}
              className="w-full mt-4 bg-burgundy hover:bg-burgundy-hover text-ink font-semibold font-mono text-xs uppercase tracking-[0.2em] py-3 rounded-lg transition-colors duration-300"
            >
              Unlock
            </button>
          </div>
        </div>
      )}

      {/* Upload picker modal */}
      {editing && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-ink/80 backdrop-blur-sm px-6">
          <div className="w-full max-w-sm rounded-2xl border border-white/10 bg-surface p-8 relative">
            <button onClick={() => setEditing(null)} className="absolute top-4 right-4 text-slate-400 hover:text-white">
              <X size={18} />
            </button>
            <h3 className="font-display text-2xl text-white">Upload testimonial {String(editing).padStart(2, "0")}</h3>
            <p className="text-slate-400 text-sm mt-2">Choose a video file (MP4/WebM recommended).</p>
            <input
              ref={fileRef}
              data-testid="video-file-input"
              type="file"
              accept="video/*"
              onChange={(e) => handleFile(editing, e.target.files?.[0])}
              className="w-full mt-5 text-sm text-slate-300 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:bg-burgundy file:text-ink file:font-mono file:text-xs file:uppercase file:tracking-widest hover:file:bg-burgundy-hover file:cursor-pointer"
            />
          </div>
        </div>
      )}
    </section>
  );
}
